import Sentry from '@/services/sentry'
import LeaderboardApi from '@/api/leaderboard'
import { isFeatureEnabled, getVariant } from './featureToggles.module'

const _state = {
  isLeaderboardVisible: false,
  leaderboard: {},
  isShowingIndividualLeaderboardForTeamPlay: false,
  isAddingPoint: false,
  // Cached per slide+subject so a ranking swap reuses the board instead of re-POSTing (re-broadcasting).
  leaderboardBySlide: {},
}

const MUTATION_TYPES = {
  TOGGLE_LEADERBOARD_VISIBILITY: 'TOGGLE_LEADERBOARD_VISIBILITY',
  SET_LEADERBOARD: 'SET_LEADERBOARD',
  TOGGLE_INDIVIDUAL_LEADERBOARD_FOR_TEAMPLAY: 'TOGGLE_INDIVIDUAL_LEADERBOARD_FOR_TEAMPLAY',
  SET_IS_ADDING_POINT: 'SET_IS_ADDING_POINT',
  SET_LEADERBOARD_CACHE: 'SET_LEADERBOARD_CACHE',
}

export const UNLEASH_FLAG_TOGGLE_LEADERBOARD_ON_PRESENTING = 'toggle-leaderboard-on-presenting'
export const UNLEASH_FLAG_NEW_LEADERBOARD = 'AHA-42758-new-leaderboard'

const _getters = {
  isTeamPlay(_, __, rootState) {
    return rootState.presentation?.teamPlay
  },
  isAddingPoint(state) {
    return state.isAddingPoint
  },
  isShowingTeamList(state, getters) {
    return getters.isTeamPlay && !state.isShowingIndividualLeaderboardForTeamPlay
  },
  isShowingIndividualLeaderboardForTeamPlay(state) {
    return state.isShowingIndividualLeaderboardForTeamPlay
  },
  canUseLeaderboardToggleOnPresenting(_, __, ___, rootGetters) {
    return isFeatureEnabled(rootGetters, UNLEASH_FLAG_TOGGLE_LEADERBOARD_ON_PRESENTING)
  },
  canUseNewLeaderboard(_, __, rootState, rootGetters) {
    if (!isFeatureEnabled(rootGetters, UNLEASH_FLAG_NEW_LEADERBOARD)) return false
    const turnOnDate = getVariant(rootGetters, UNLEASH_FLAG_NEW_LEADERBOARD)?.payload?.value
    const { presentation } = rootState
    if (!turnOnDate || !presentation) return false
    // gate by last reset (if available) or createdAt
    const resetTimestamp = Number(presentation.resetTimeStamp)
    const boundary = Number.isFinite(resetTimestamp) && resetTimestamp > 0 ? resetTimestamp : presentation.createdAt
    if (!boundary) return false
    if (new Date(boundary) <= new Date(turnOnDate)) return false
    // gate to decks that actually contain a plugin quiz slide
    return (presentation.slides || []).some((slide) => rootGetters['embedApp/isScoredSlide'](slide))
  },

  isShowLeaderboardVisibilityToggle(state, getters, rootState, rootGetters) {
    if (!getters.canUseLeaderboardToggleOnPresenting) return false
    const slides = rootState.presentation?.slides
    if (!slides || slides.length == 0) return false
    return rootGetters.isPresentingOrBackstage && slides.some((slide) => rootGetters['slideModule/isQuizSlide'](slide))
  },

  isLeaderboardVisible(state, getters, _, rootGetters) {
    if (!getters.canUseLeaderboardToggleOnPresenting) return false
    if (!rootGetters.isPresentingOrBackstage) return false
    return state.isLeaderboardVisible
  },

  isLeaderboardEnabled() {
    return (slide) => {
      return slide.isLeaderboardEnabled
    }
  },
  leaderboardData(state) {
    return state.leaderboard
  },

  rankingListCount(_, getters) {
    return getters?.rankingList?.length
  },

  rankingList(state, getters) {
    if (getters.isShowingTeamList) {
      return state.leaderboard?.teams
    }
    return state.leaderboard?.audiences
  },

  rankingListKey(state, getters) {
    return (item) => {
      if (getters.isShowingTeamList) {
        return `team-${item.id}`
      }
      return `player-${item.audience}`
    }
  },

  topPoint(state, getters) {
    return getters.rankingList[0].totalPoint
  },

  getPercentage(state, getters) {
    return (item) => {
      return `${(item.totalPoint / getters.topPoint) * 100}%`
    }
  },

  winnerStr(state, getters) {
    if (getters.isShowingTeamList) {
      return state.leaderboard.topTeamStr
    }
    return state.leaderboard.topAudienceStr
  },

  winnerCount(state, getters) {
    if (getters.isShowingTeamList) {
      return state.leaderboard.countTeamWinner
    }
    return state.leaderboard.countWinner
  },

  getItemName(state, getters) {
    return (item) => {
      if (getters.isShowingTeamList) {
        return item.name
      }
      return item.audienceName
    }
  },

  getManualPointData(state, getters, rootState, rootGetters) {
    const isShownEmojiAvatar = rootGetters['presentationSetting/isShownEmojiAvatar']

    return (item) => {
      const data = {
        slideId: rootState.slideActive.id,
        presentationId: rootState.presentation.id,
        totalPoint: item.totalPoint,
      }
      if (getters.isShowingTeamList) {
        data.teamId = item.id
        data.name = item.name
      } else {
        data.audienceId = item.audience
        data.name = isShownEmojiAvatar ? `${item.audienceEmoji} ${item.audienceName}` : item.audienceName
      }
      return data
    }
  },
}

const slidesUpTo = (slides, order, include) => (slides || [])
  .filter((slide) => include(slide) && (order === undefined || slide.order <= order))
  .sort((a, b) => a.order - b.order)

const scoredSlideIdsUpTo = (slides, order, isQuizSlide) => slidesUpTo(
  slides, order, (slide) => isQuizSlide(slide) || slide.type === 'leaderboard',
).map((slide) => Number(slide.id))

const getLastLeaderboard = (slides, currentSlideId, order) => {
  if (!slides) return null
  let last = null
  slides.forEach((slide) => {
    if (slide.type === 'leaderboard' && slide.id !== currentSlideId && (order === undefined || slide.order <= order)) {
      if (!last || slide.order > last.order) {
        last = slide
      }
    }
  })
  return last
}

const actions = {
  // show/hide the leaderboard during the quiz
  toggleLeaderboardVisibilityAction: async ({ commit, getters }, { isVisible }) => {
    if (!getters.canUseLeaderboardToggleOnPresenting) return
    commit(MUTATION_TYPES.TOGGLE_LEADERBOARD_VISIBILITY, isVisible)
  },

  toggleLeaderboardOnParticipantScreenAction: async ({ dispatch }, { presentation, isVisible }) => {
    dispatch('kvSync/createKeyValueAction', {
      path: `/leaderboards/${presentation.id}`,
      end_part: '',
      type: 'string',
      value: JSON.stringify({ isVisible }),
    }, { root: true })
  },

  fetchLeaderboardAction: async ({ commit, getters, rootState }, payload) => {
    let leaderboard
    if (getters.canUseNewLeaderboard) {
      leaderboard = await LeaderboardApi.getLeaderboardTopN({
        presentationId: payload.presentationId,
        presentationVersion: rootState.presentation.version,
        subject: getters.isShowingTeamList ? 'team' : 'participant',
        teamScoringRule: rootState.presentation.teamScoringRule,
        n: 100,
      })
    } else {
      leaderboard = await LeaderboardApi.getLatestLeaderboard(payload)
    }
    commit(MUTATION_TYPES.SET_LEADERBOARD, leaderboard)
    return leaderboard
  },

  fetchLeaderboardSlideAction: async ({ getters, rootState, rootGetters }, payload) => {
    const {
      presentationId,
      subject,
      slideId,
      order,
    } = payload
    let leaderboard
    if (getters.canUseNewLeaderboard) {
      const { slides, version: presentationVersion } = rootState.presentation
      const isQuizSlide = rootGetters['slideModule/isQuizSlide']
      const slideIds = scoredSlideIdsUpTo(slides, order, isQuizSlide)
      if (!slideIds.length) {
        return subject === 'team'
          ? { teams: [], countTeamWinner: 0, topTeamStr: '' }
          : { audiences: [], countWinner: 0, topAudienceStr: '' }
      }
      const lastLeaderboard = getLastLeaderboard(slides, slideId, order)
      const [board, streaks] = await Promise.all([
        LeaderboardApi.createLeaderboardSlideSnapshot({
          presentationId,
          presentationVersion,
          slideId,
          slideIds,
          lastSlideId: lastLeaderboard ? Number(lastLeaderboard.id) : undefined,
          subject,
          teamScoringRule: rootState.presentation.teamScoringRule,
        }),
        subject === 'participant'
          ? LeaderboardApi.getLeaderboardSlideStreaks({ presentationId, presentationVersion, slideIds })
            .catch((err) => { Sentry.captureException(err); return {} })
          : Promise.resolve({}),
      ])
      leaderboard = { ...board, ...streaks }
    } else if (getters.canUseLeaderboardToggleOnPresenting) {
      leaderboard = await LeaderboardApi.getLatestLeaderboard({ presentationId }, { slideId })
    } else {
      leaderboard = await LeaderboardApi.createSlideLeaderboardIndividual(payload)
    }
    return leaderboard
  },

  // turn on/off the leaderboard for quiz slide in Editor
  toggleLeaderboardEnabledAction: async ({ dispatch }, { slide }) => {
    dispatch('updateSlideActiveActionV2', {
      slide: {
        ...slide,
        isLeaderboardEnabled: !slide.isLeaderboardEnabled,
      },
      $optimistic: true,
    }, { root: true })
  },

  toggleIndividualLeaderboardForTeamplayAction: async ({ commit }) => {
    commit(MUTATION_TYPES.TOGGLE_INDIVIDUAL_LEADERBOARD_FOR_TEAMPLAY)
  },

  addPointAction: async ({ commit, getters, rootState }, {
    point, audienceId, teamId, presentationId, slideId,
  }) => {
    commit(MUTATION_TYPES.SET_IS_ADDING_POINT, true)
    if (getters.canUseNewLeaderboard && point) {
      // Await first — the success refresh POSTs a recomputed snapshot that would omit a not-yet-landed bonus.
      try {
        await LeaderboardApi.createBonusResult({
          presentationId,
          presentationVersion: rootState.presentation.version,
          audienceId,
          teamId,
          slideId,
          point,
        })
      } catch (error) {
        Sentry.captureException(error)
      }
    }
    const res = await LeaderboardApi.addManualPoint({
      point,
      audienceId,
      teamId,
      presentationId,
      slideId,
    })
    commit(MUTATION_TYPES.SET_IS_ADDING_POINT, false)
    return res
  },

}

const mutations = {
  [MUTATION_TYPES.TOGGLE_LEADERBOARD_VISIBILITY](state, isVisible) {
    if (typeof isVisible !== 'boolean') {
      state.isLeaderboardVisible = !state.isLeaderboardVisible
      return
    }
    state.isLeaderboardVisible = isVisible
  },

  [MUTATION_TYPES.SET_LEADERBOARD](state, leaderboard) {
    state.leaderboard = leaderboard
  },

  [MUTATION_TYPES.TOGGLE_INDIVIDUAL_LEADERBOARD_FOR_TEAMPLAY](state) {
    state.isShowingIndividualLeaderboardForTeamPlay = !state.isShowingIndividualLeaderboardForTeamPlay
  },

  [MUTATION_TYPES.SET_IS_ADDING_POINT](state, isAddingPoint) {
    state.isAddingPoint = isAddingPoint
  },

  [MUTATION_TYPES.SET_LEADERBOARD_CACHE](state, { slideId, subject, leaderboard }) {
    state.leaderboardBySlide = { ...state.leaderboardBySlide, [`${slideId}:${subject}`]: leaderboard }
  },
}

const leaderboardModule = {
  namespaced: true,
  state: _state,
  getters: _getters,
  actions,
  mutations,
}

export default leaderboardModule
